import type { WorkspaceReadiness } from "@carbon-operations/contracts";

export const ReadinessPollIntervalMs = 1_500;
export const ReadinessPollMaxReads = 12;

export type ReadinessVerificationOutcome =
  | { kind: "ready"; readiness: WorkspaceReadiness }
  | { kind: "blocked"; readiness: WorkspaceReadiness; detail: string }
  | { kind: "timeout"; readiness: WorkspaceReadiness }
  | { kind: "cancelled"; readiness: WorkspaceReadiness }
  | { kind: "error"; readiness: WorkspaceReadiness; error: Error };

export type ReadinessMutationLock = { inFlight: boolean };

export async function runSingleReadinessMutation<T>(
  lock: ReadinessMutationLock,
  operation: () => Promise<T>,
): Promise<{ started: false } | { started: true; value: T }> {
  if (lock.inFlight) return { started: false };
  lock.inFlight = true;
  try {
    return { started: true, value: await operation() };
  } finally {
    lock.inFlight = false;
  }
}

export async function verifyQueuedReadiness(
  options: {
    initial: WorkspaceReadiness;
    read: (signal: AbortSignal) => Promise<WorkspaceReadiness>;
    onUpdate: (readiness: WorkspaceReadiness) => void;
    signal: AbortSignal;
    intervalMs?: number;
    maxReads?: number;
    wait?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
  },
): Promise<ReadinessVerificationOutcome> {
  const intervalMs = options.intervalMs ?? ReadinessPollIntervalMs;
  const maxReads = options.maxReads ?? ReadinessPollMaxReads;
  const wait = options.wait ?? waitForDelay;
  let current = options.initial;

  for (let readCount = 0; readCount <= maxReads; readCount += 1) {
    const terminal = terminalOutcome(current);
    if (terminal) return terminal;
    if (options.signal.aborted) {
      return { kind: "cancelled", readiness: current };
    }
    if (readCount === maxReads) return { kind: "timeout", readiness: current };

    try {
      await wait(intervalMs, options.signal);
      if (options.signal.aborted) {
        return { kind: "cancelled", readiness: current };
      }
      current = await options.read(options.signal);
      options.onUpdate(current);
    } catch (cause) {
      if (options.signal.aborted || isAbortError(cause)) {
        return { kind: "cancelled", readiness: current };
      }
      return {
        kind: "error",
        readiness: current,
        error: cause instanceof Error
          ? cause
          : new Error("Readiness verification stopped unexpectedly"),
      };
    }
  }

  return { kind: "timeout", readiness: current };
}

function terminalOutcome(
  readiness: WorkspaceReadiness,
): ReadinessVerificationOutcome | null {
  if (readiness.state === "ready" && readiness.execution_enabled) {
    return { kind: "ready", readiness };
  }
  const blocked = readiness.checks.find((check) => check.status === "blocked");
  if (blocked) {
    return { kind: "blocked", readiness, detail: blocked.detail };
  }
  return null;
}

function waitForDelay(
  milliseconds: number,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const aborted = () => {
      globalThis.clearTimeout(timer);
      reject(
        new DOMException("Readiness verification cancelled", "AbortError"),
      );
    };
    const timer = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", aborted);
      resolve();
    }, milliseconds);
    if (signal.aborted) {
      aborted();
      return;
    }
    signal.addEventListener("abort", aborted, { once: true });
  });
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof DOMException && cause.name === "AbortError";
}
